import { useState } from "react";
import { toast } from "sonner";
import { FadeUp } from "./Reveal";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#C9A26B] transition-colors duration-300 text-sm";

export const BookingForm = () => {
  const [form, setForm] = useState({ names: "", email: "", whatsapp: "", wedding_date: "", venue: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Your story reached us.", {
        description: "We reply to every inquiry within 48 hours.",
      });
      setForm({ names: "", email: "", whatsapp: "", wedding_date: "", venue: "", message: "" });
    } catch (err) {
      toast.error("Could not send your inquiry", { description: formatApiError(err) });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="relative overflow-hidden px-6 md:px-12 py-28 md:py-44 bg-[#121212] border-t border-white/5" data-testid="booking-section">
      <img src="/photos/couple-forest.webp" alt="" aria-hidden="true" className="photo-tone absolute inset-0 w-full h-full object-cover opacity-[0.12]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8">
        <FadeUp className="md:col-span-5">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Booking Inquiry</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5F4F0] leading-[1.05] mb-6">
            Tell us <span className="font-accent italic text-[#C9A26B]">your story</span>
          </h2>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A26B] mb-10" data-testid="booking-2027-note">Now booking 2027 — Italy · Denmark · Sweden</p>
          <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed max-w-md mb-12">
            We take on a limited number of weddings each year so every couple gets
            our full hearts. Tell us about your day — the place, the people, the feeling you're after.
          </p>
          <div className="flex flex-col gap-4 text-sm">
            <a href="mailto:tapeshiftinfo@gmail.com" data-testid="contact-email" className="text-[#F5F4F0] hover:text-[#C9A26B] transition-colors duration-300">
              tapeshiftinfo@gmail.com
            </a>
            <a href="https://instagram.com/tapeshift_weddings" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-[#F5F4F0] hover:text-[#C9A26B] transition-colors duration-300">
              @tapeshift_weddings
            </a>
            <p className="text-[#525252]">Based in Italy & Copenhagen, Denmark — available worldwide</p>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="md:col-span-6 md:col-start-7">
          <form onSubmit={submit} className="flex flex-col gap-10" data-testid="booking-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="names" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Your names *</label>
                <input id="names" data-testid="booking-names-input" required value={form.names} onChange={set("names")} placeholder="Alex & Sam" className={inputCls} />
              </div>
              <div>
                <label htmlFor="email" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Email *</label>
                <input id="email" type="email" data-testid="booking-email-input" required value={form.email} onChange={set("email")} placeholder="you@love.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="date" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Wedding date</label>
                <input id="date" type="date" data-testid="booking-date-input" value={form.wedding_date} onChange={set("wedding_date")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="venue" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Venue / city</label>
                <input id="venue" data-testid="booking-venue-input" value={form.venue} onChange={set("venue")} placeholder="Tuscany, Italy" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="whatsapp" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">WhatsApp number *</label>
              <input id="whatsapp" type="tel" required data-testid="booking-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+1 555 000 0000" className={inputCls} />
            </div>
            <div>
              <label htmlFor="message" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Your story</label>
              <textarea id="message" rows={4} data-testid="booking-message-input" value={form.message} onChange={set("message")} placeholder="How you met, what you're planning, what matters most…" className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={sending}
              className="self-start text-xs tracking-[0.35em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-12 py-5 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send Inquiry"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
